/**
 * Shared mixin for IQTools pages (Workflows, Dialogs) that display
 * a Vue Flow diagram with expandable / editable nodes.
 *
 * Provides:
 *  - handleExpandNode   – fetches subgraph data and adds it to the diagram
 *  - handleEditNode     – opens the appropriate editor for a clicked node
 *  - handleCommandUpdated – updates command node title in-place via Vue Flow API
 *  - loadBuildingKit    – lazy-loads building-kit dependencies
 *
 * Requirements on the consuming component:
 *  - data: nodes, edges, containers, commands, expandedNodeIds,
 *          showEditCommandDialog, selectedCommand, kitLoaded, editingFlowNodeId
 *  - refs: diagram (IqToolsDiagram)
 *  - methods: onActionItemClick (for dialog/workflow navigation)
 *  - mapActions: getFunctions (from buildingKit)
 */

import { isNil } from 'lodash';
import { getWorkflowDiagramInfo, getDialogDiagramInfo } from './DiagramHelper';
import {
  IQ_TOOL_ACTION_TYPE_COMMAND,
  IQ_TOOL_ACTION_TYPE_DIALOG,
  IQ_TOOL_ACTION_TYPE_WORKFLOW,
  IQ_TOOL_DIAGRAM_EDITABLE_NODE_TYPES,
  IQ_TOOL_DIAGRAM_EXPANDABLE_NODE_TYPES,
} from '@/constants/iqtool';
import {
  AGENT_LIMIT,
  TEAM_LIMIT,
  TAG_LIMIT,
  CATEGORY_LIMIT,
  ASSETS_LIMIT,
  DOCS_LIMIT,
} from '@/constants/pagination';

function getDiagramNodes(vm) {
  const diagram = vm.$refs.diagram;
  return (diagram && Array.isArray(diagram.nodes)) ? diagram.nodes : vm.nodes;
}

function buildExpandEdge(sourceId, targetId) {
  return {
    source: sourceId,
    target: targetId,
    sourceHandle: 'source-bottom',
    targetHandle: 'target-top',
    zIndex: -1,
    style: { stroke: '#9ca3af', strokeWidth: 1, strokeDasharray: '5,5' },
  };
}

function attachNodeHandlers(vm, nodes) {
  return nodes.map((node, index) => {
    const out = { ...node, data: { ...(node.data || {}) } };

    if (IQ_TOOL_DIAGRAM_EDITABLE_NODE_TYPES.includes(node.type)) {
      out.data.isEditable = true;
      out.data.onEditNode = (type, id, name, flowNodeId) => {
        vm.handleEditNode({ type, id, name, flowNodeId });
      };
    }

    if (IQ_TOOL_DIAGRAM_EXPANDABLE_NODE_TYPES.includes(node.type)) {
      out.data.onExpandNode = (flowId) => vm.handleExpandNode({ flowNodeId: flowId });
    } else {
      out.data.hideExpand = true;
    }

    if (index === 0) {
      out.class = out.class ? `${out.class} expanded-root-node` : 'expanded-root-node';
    }

    return out;
  });
}

export default {
  data() {
    return {
      expandingNode: false,
    };
  },
  methods: {
    async handleExpandNode({ flowNodeId }) {
      try {
        if (!flowNodeId) return;

        const clickedNode = getDiagramNodes(this).find((n) => n.id === flowNodeId);
        if (!clickedNode) return;

        const { type } = clickedNode;
        const numericId = !isNil(clickedNode.data?.id) ? Number(clickedNode.data.id) : NaN;
        if (Number.isNaN(numericId)) return;

        this.expandingNode = true;

        let diagramData;
        if (type === IQ_TOOL_ACTION_TYPE_WORKFLOW) {
          const resp = await this.$store.dispatch('workflows/selectWorkflow', numericId);
          diagramData = getWorkflowDiagramInfo(resp.data);
        } else if (type === IQ_TOOL_ACTION_TYPE_DIALOG) {
          await this.$store.dispatch('dialogs/selectDialog', numericId);
          const dialogData = this.$store.getters['dialogs/selected'];
          if (!dialogData) return;
          diagramData = getDialogDiagramInfo(dialogData);
        } else {
          return;
        }

        // Reset the store that was used only for fetching expansion data.
        // The current page's own store is managed by its lifecycle (beforeUnmount),
        // so only reset the *other* store to avoid stale selections on navigation.
        const isWorkflowsPage = this.$options.name === 'workflows';
        if (type === IQ_TOOL_ACTION_TYPE_DIALOG && isWorkflowsPage) {
          this.$store.dispatch('dialogs/resetSelected');
        }
        if (type === IQ_TOOL_ACTION_TYPE_WORKFLOW && !isWorkflowsPage) {
          this.$store.dispatch('workflows/resetSelected');
        }

        if (!diagramData?.nodes?.length) return;

        // Merge subgraph commands so edit works for nodes in expanded subgraphs
        if (diagramData.commands?.length) {
          const existingIds = new Set((this.commands || []).map((c) => c.id));
          for (const cmd of diagramData.commands) {
            if (!existingIds.has(cmd.id)) {
              this.commands.push(cmd);
            }
          }
        }

        const rootNode = diagramData.nodes[0];
        const nodesWithHandlers = attachNodeHandlers(this, diagramData.nodes);

        this.expandedNodeIds = [
          ...this.expandedNodeIds,
          ...[clickedNode.id, rootNode.id].filter((id) => !this.expandedNodeIds.includes(id)),
        ];

        this.$refs.diagram?.addSubGraph({
          nodes: nodesWithHandlers,
          edges: [buildExpandEdge(clickedNode.id, rootNode.id), ...(diagramData.edges || [])],
          containers: diagramData.containers || [],
          directAttachmentSourceId: clickedNode.id,
          expandedNodeIds: this.expandedNodeIds,
        });
      } catch {
        const fallbackMsg = 'Unable to expand node';
        const i18nKey = this.$options.name === 'workflows'
          ? 'iq_tools_tab.workflows.unable_to_load'
          : 'iq_tools_tab.dialogs.unable_to_load';
        this.$aiq.notify.error(this.$t(i18nKey) || fallbackMsg);
      } finally {
        this.expandingNode = false;
      }
    },

    async handleEditNode({ type, id, name, flowNodeId }) {
      this.editingFlowNodeId = flowNodeId;
      id = Number(id);

      switch (type) {
        case IQ_TOOL_ACTION_TYPE_COMMAND:
          this.selectedCommand = (this.commands || []).find((c) => c.id === id) || {};
          if (this.selectedCommand.id) {
            await this.loadBuildingKit();
            this.showEditCommandDialog = true;
          }
          break;

        case IQ_TOOL_ACTION_TYPE_DIALOG:
          if (typeof this.editDialog === 'function') {
            await this.editDialog(id, true);
          } else {
            await this.onActionItemClick({ id, name, unit_type: IQ_TOOL_ACTION_TYPE_DIALOG });
          }
          break;

        case IQ_TOOL_ACTION_TYPE_WORKFLOW:
          if (typeof this.editWorkflow === 'function') {
            this.editWorkflow(id, true);
          } else {
            await this.onActionItemClick({ id, name, unit_type: IQ_TOOL_ACTION_TYPE_WORKFLOW });
          }
          break;
      }
    },

    handleCommandUpdated({ customName } = {}) {
      if (this.selectedCommand?.id && customName) {
        const diagram = this.$refs.diagram;
        if (diagram?.updateNodeDataByTypeAndId) {
          diagram.updateNodeDataByTypeAndId(
            IQ_TOOL_ACTION_TYPE_COMMAND,
            this.selectedCommand.id,
            { title: customName, label: customName },
          );
        }

        // Keep commands array in sync so re-editing shows the updated title
        const cmd = (this.commands || []).find((c) => c.id === this.selectedCommand.id);
        if (cmd?.payload) {
          cmd.payload.custom_name = customName;
        }

        this.editingFlowNodeId = null;
        return Promise.resolve();
      }
      return Promise.resolve();
    },

    async loadBuildingKit() {
      if (this.kitLoaded) return;

      await Promise.all([
        this.$store.dispatch('agents/getAgentsList', [{ limit: AGENT_LIMIT }]),
        this.$store.dispatch('tags/getTagsList', [{ limit: TAG_LIMIT }]),
        this.$store.dispatch('categories/getCategoriesList', [{ limit: CATEGORY_LIMIT }]),
        this.$store.dispatch('teams/getTeamsList', [{ limit: TEAM_LIMIT }]),
        this.$store.dispatch('assets/getAssetsList', [{ limit: ASSETS_LIMIT }]),
        this.$store.dispatch('documents/getDocumentsList', [{ limit: DOCS_LIMIT }]),
      ]);

      this.getFunctions(this.$store);
      this.kitLoaded = true;
    },
  },
};
